import { useState, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useParams, useSearchParams } from "react-router-dom";
import { useGetSystemComplianceHistoryTabDataById } from "@features/ax/assetRegistry/systems/hooks";
import { PageHeader } from "@shared/components/PageHeader";
import { Eye } from "lucide-react";
import { ComplianceReportDialog } from "@features/ax/shared/complianceReport";

// History Landing Component with both tables
export const HistoryLanding = () => {
  const { systemId } = useParams();
  const { data: historyData } = useGetSystemComplianceHistoryTabDataById(
    Number(systemId),
    2
  );
  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedReportData, setSelectedReportData] = useState();

  const handleViewReport: (row: any) => void = (row: any) => {
    setIsDialogOpen(true);
    setSelectedReportData(row);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedReportData(null);
  };

  // Compliance History Table Columns
  const complianceHistoryColumns: TableColumn<any>[] = [
    {
      key: "reportDate",
      header: "Report Date",
      type: "text",
      render: (value) => <span className="font-medium">{value}</span>,
    },
    {
      key: "analysisPeriod",
      header: "Analysis Period",
      type: "text",
      render: (value) => <span className="text-sm text-gray-600">{value}</span>,
    },
    {
      key: "parametersMonitored",
      header: "Parameters Monitored",
      type: "custom",
      render: (value) => (
        <Badge variant="outline" className="bg-blue-50 text-blue-700">
          {value}
        </Badge>
      ),
    },
    {
      key: "inCompliance",
      header: "In Compliance",
      type: "custom",
      render: (value) => (
        <Badge className="bg-green-100 text-green-800">{value}</Badge>
      ),
    },
    {
      key: "violations",
      header: "Violations",
      type: "custom",
      render: (value) => (
        <Badge
          className={
            value > 0
              ? "bg-red-100 text-red-800"
              : "bg-green-100 text-green-800"
          }
        >
          {value}
        </Badge>
      ),
    },
    {
      key: "assessedBy",
      header: "Assessed By",
      type: "text",
      render: (value) => <span className="text-gray-600">{value}</span>,
    },
    {
      key: "actions",
      header: "Actions",
      render: (value, row) => {
        return (
          <div className="flex items-center gap-2">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => handleViewReport(row)}
              tooltip="View Report"
            >
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  const complianceTotalPages = Math.ceil(historyData?.count / PAGE_SIZE);
  const complianceStartIndex = (currentPage - 1) * PAGE_SIZE;

  const compliancePagination = useMemo(
    () => ({
      currentPage: currentPage,
      totalPages: complianceTotalPages,
      totalItems: historyData?.count,
      itemsPerPage: PAGE_SIZE,
      startItem: complianceStartIndex + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, historyData?.count),
    }),
    [
      currentPage,
      complianceTotalPages,
      historyData?.count,
      complianceStartIndex,
    ]
  );

  const handleCompliancePageChange = useCallback(
    (newPage: number) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", newPage.toString());

      setSearchParams(newParams, { replace: true });
      setCurrentPage(newPage);
    },
    [searchParams, setSearchParams]
  );

  return (
    <div className="space-y-6">
      {/* Compliance Calculation History */}
      <Card>
        <CardHeader>
          <PageHeader
            title="Compliance Calculation History"
            subtitle="Historical record of calculated compliance reports"
          />
        </CardHeader>
        <CardContent className="p-0 mt-3">
          <GenericTable
            data={historyData?.results}
            columns={complianceHistoryColumns}
            rowKey="id"
            pagination={compliancePagination}
            onPageChange={handleCompliancePageChange}
            emptyMessage="No compliance history found"
          />
        </CardContent>
      </Card>
      <ComplianceReportDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        reportData={selectedReportData}
      />
    </div>
  );
};
